import React, { useState, useEffect } from 'react';
import { providersAPI } from '../../api/providers';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import { DAYS_OF_WEEK } from '../../utils/constants';
import toast from 'react-hot-toast';

// Generate time options every 30 minutes from 00:00 to 23:30
const TIME_OPTIONS = [];
for (let h = 0; h < 24; h++) {
  for (let m = 0; m < 60; m += 30) {
    const value = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    const ampm = h < 12 ? 'AM' : 'PM';
    const label = `${hour12}:${String(m).padStart(2, '0')} ${ampm}`;
    TIME_OPTIONS.push({ value, label });
  }
}

const ManageAvailability = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [provId, setProvId] = useState(null);
  const [availability, setAvailability] = useState([]);
  const [savedSchedule, setSavedSchedule] = useState([]);

  useEffect(() => {
    fetchAvailability();
  }, []);

  const fetchAvailability = async () => {
    setLoading(true);
    try {
      const profile = await providersAPI.getMyProfile();
      setProvId(profile.id);
      const data = await providersAPI.getAvailability(profile.id);
      setAvailability(data || []);
    } catch (error) {
      console.error('Error fetching availability:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDayToggle = (dayIndex) => {
    const existing = availability.find((a) => a.dayOfWeek === dayIndex);
    if (existing) {
      setAvailability(availability.filter((a) => a.dayOfWeek !== dayIndex));
    } else {
      setAvailability([
        ...availability,
        {
          dayOfWeek: dayIndex,
          startTime: '09:00',
          endTime: '17:00',
          breakStart: '',
          breakEnd: '',
          isActive: true,
        },
      ]);
    }
  };

  const updateDayAvailability = (dayIndex, field, value) => {
    setAvailability(
      availability.map((a) =>
        a.dayOfWeek === dayIndex ? { ...a, [field]: value } : a
      )
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Save each active day one by one
      for (const slot of availability) {
        const data = {
          dayOfWeek: slot.dayOfWeek,
          startTime: slot.startTime,
          endTime: slot.endTime,
        };
        // Only include break times if both are set
        if (slot.breakStart && slot.breakEnd) {
          data.breakStart = slot.breakStart;
          data.breakEnd = slot.breakEnd;
        }
        await providersAPI.saveAvailability(data);
      }
      toast.success('Availability saved!');
      // Store saved schedule for summary, then reset form
      setSavedSchedule([...availability]);
      setAvailability([]);
      // Re-fetch from backend to show saved data in summary
      const profile = await providersAPI.getMyProfile();
      const fresh = await providersAPI.getAvailability(profile.id);
      setSavedSchedule(fresh || []);
    } catch (error) {
      console.error('Save error:', error);
      // Toast already shown by axios interceptor
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="large" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-background)' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-xl sm:text-2xl mb-2" style={{ color: 'var(--gray-12)' }}>Manage Availability</h1>
          <p style={{ color: 'var(--gray-11)', textAlign: 'center' }}>Set your working hours for each day of the week</p>
        </div>

        <div className="flex gap-6" style={{ alignItems: 'stretch' }}>
        {/* Left: Schedule form */}
        <div className="flex-1 rounded-xl p-6" style={{ backgroundColor: 'var(--gray-2)', border: '1px solid var(--gray-4)' }}>
          <h3 style={{ fontSize: '16px', fontWeight: 600, color: 'var(--gray-12)', marginBottom: '16px', textAlign: 'left' }}>
            Weekly Schedule
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {DAYS_OF_WEEK.map((day, index) => {
              const dayAvailability = availability.find((a) => a.dayOfWeek === index);
              const isActive = !!dayAvailability;

              return (
                <div
                  key={index}
                  className="rounded-lg p-4"
                  style={{ border: '1px solid var(--gray-4)', backgroundColor: isActive ? 'var(--color-background)' : 'var(--gray-3)' }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <label className="flex items-center gap-3" style={{ cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={isActive}
                        onChange={() => handleDayToggle(index)}
                        style={{ width: '18px', height: '18px', accentColor: 'var(--orange-9)' }}
                      />
                      <span style={{ fontWeight: isActive ? 600 : 400, color: isActive ? 'var(--gray-12)' : 'var(--gray-9)', fontSize: '15px' }}>
                        {day}
                      </span>
                    </label>
                    {isActive && (
                      <span style={{ fontSize: '13px', color: 'var(--orange-9)', fontWeight: 500 }}>
                        {dayAvailability.startTime} – {dayAvailability.endTime}
                      </span>
                    )}
                  </div>

                  {isActive && (() => {
                    const start = dayAvailability.startTime;
                    const end = dayAvailability.endTime;
                    const breakOpts = TIME_OPTIONS.filter(t => t.value > start && t.value < end);

                    return (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" style={{ marginLeft: '30px' }}>
                        {[
                          { label: 'Start', field: 'startTime', value: start, options: TIME_OPTIONS },
                          { label: 'End', field: 'endTime', value: end, options: TIME_OPTIONS.filter(t => t.value > start) },
                          { label: 'Break Start', field: 'breakStart', value: dayAvailability.breakStart || '', options: breakOpts },
                          { label: 'Break End', field: 'breakEnd', value: dayAvailability.breakEnd || '', options: dayAvailability.breakStart ? breakOpts.filter(t => t.value > dayAvailability.breakStart) : breakOpts },
                        ].map(({ label, field, value, options }) => (
                          <div key={field}>
                            <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, color: 'var(--gray-10)', marginBottom: '4px', textAlign: 'left' }}>
                              {label}
                            </label>
                            <select
                              value={value}
                              onChange={(e) => updateDayAvailability(index, field, e.target.value)}
                              style={{
                                width: '100%', padding: '6px 8px', borderRadius: '6px',
                                border: '1px solid var(--gray-6)', backgroundColor: 'var(--color-background)',
                                color: 'var(--gray-12)', fontSize: '14px', cursor: 'pointer',
                              }}
                            >
                              {field.startsWith('break') && <option value="">None</option>}
                              {options.map(t => (
                                <option key={t.value} value={t.value}>{t.label}</option>
                              ))}
                            </select>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              );
            })}
          </div>

          <div className="flex justify-end mt-6">
            <Button onClick={handleSave} loading={saving} disabled={availability.length === 0}>
              Save Availability
            </Button>
          </div>
        </div>

        {/* Vertical divider */}
        {savedSchedule.length > 0 && (
          <div style={{ width: '1px', backgroundColor: 'var(--orange-6)', flexShrink: 0 }} />
        )}

        {/* Right: Saved Schedule Summary */}
        {savedSchedule.length > 0 && (
          <div style={{ width: '320px', flexShrink: 0 }}>
            <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--gray-2)', border: '1px solid var(--gray-4)', position: 'sticky', top: '80px' }}>
              <h3 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--gray-12)', marginBottom: '14px', textAlign: 'left' }}>
                Your Schedule
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {savedSchedule
                  .sort((a, b) => a.dayOfWeek - b.dayOfWeek)
                  .map(slot => {
                    const h12 = (t) => {
                      const [h, m] = t.split(':');
                      const hr = parseInt(h);
                      return `${hr === 0 ? 12 : hr > 12 ? hr - 12 : hr}:${m} ${hr < 12 ? 'AM' : 'PM'}`;
                    };
                    return (
                      <div
                        key={slot.dayOfWeek}
                        className="py-2 px-3 rounded-lg"
                        style={{ backgroundColor: 'var(--color-background)', border: '1px solid var(--gray-4)' }}
                      >
                        <div style={{ fontWeight: 600, color: 'var(--gray-12)', fontSize: '13px', marginBottom: '2px', textAlign: 'left' }}>
                          {DAYS_OF_WEEK[slot.dayOfWeek]}
                        </div>
                        <div style={{ fontSize: '13px', color: 'var(--orange-9)', fontWeight: 500, textAlign: 'left' }}>
                          {h12(slot.startTime)} – {h12(slot.endTime)}
                        </div>
                        {slot.breakStart && slot.breakEnd && (
                          <div style={{ fontSize: '11px', color: 'var(--gray-10)', fontStyle: 'italic', marginTop: '2px', textAlign: 'left' }}>
                            Break: {h12(slot.breakStart)} – {h12(slot.breakEnd)}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default ManageAvailability;
